'use client'

import React, { useCallback, useState } from 'react'
import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import type { Page, Project } from '@/payload-types'

type FAQBlockProps =
  | Extract<NonNullable<Page['standardLayout']>[number], { blockType: 'faq' }>
  | Extract<NonNullable<Project['layout']>[number], { blockType: 'faq' }>

interface FAQItem {
  question: string
  answer: any
}

function ChevronDown() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-text-primary">
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function FAQBlock({ header, items, settings }: FAQBlockProps) {
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const handleToggle = useCallback((index: number) => {
    setActiveIndex((prev) => (prev === index ? -1 : index))
  }, [])

  const normalizedSettings = normalizeSectionSettings(settings)

  return (
    <SectionWrapper settings={normalizedSettings}>
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-6 items-start">
        {/* Left column */}
        <div className="w-full lg:w-2/5 lg:min-w-80 flex flex-col gap-6 lg:gap-8">
          {header && (header.infoTagText || header.title || header.subtitle) && (
            <SectionHeader
              infoTagText={header.infoTagText || ''}
              title={header.title || ''}
              size="md"
              subtitle={header.subtitle || undefined}
              align="left"
            />
          )}
        </div>

        {/* Right column */}
        <div className="w-full lg:w-3/5 flex flex-col">
          {items?.map((item: FAQItem, index: number) => {
            const isOpen = activeIndex === index
            const panelId = `faq-panel-${index}`
            const triggerId = `faq-trigger-${index}`
            return (
              <MotionFadeIn key={index} delay={index * 0.05} className="w-full">
                <div className="border-b border-gray-300 border-solid">
                  <button
                    type="button"
                    onClick={() => handleToggle(index)}
                    className="w-full py-4 flex items-center justify-between text-left hover:opacity-70 transition-opacity duration-200 focus:outline-none"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    id={triggerId}
                  >
                    <span className="font-medium text-lg leading-relaxed text-text-primary pr-4">
                      {item.question}
                    </span>
                    <span
                      className={cn(
                        'flex-shrink-0 transition-transform duration-400 ease-out',
                        isOpen && 'rotate-180',
                      )}
                      aria-hidden
                    >
                      <ChevronDown />
                    </span>
                  </button>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    className={cn(
                      'grid transition-[grid-template-rows,opacity] duration-500 ease-out',
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="pb-8">
                        <RichText data={item.answer} enableGutter={false} enableProse={true} />
                      </div>
                    </div>
                  </div>
                </div>
              </MotionFadeIn>
            )
          })}
        </div>
      </div>
    </SectionWrapper>
  )
}
